import { Injectable } from '@angular/core';
import { keyBy, TypedDictionary } from '@core/utils/key-by';
import { ComponentStore } from '@ngrx/component-store';
import { Legend } from '@shared/legend';
import {
  codesToAirports,
  extractAndSortAirports,
} from '@store/origins/api/origins.utils';
import { Airport, CountryWithAirports } from '@store/origins/types';
import { JbAutocompleteOption } from 'jb-component-library';
import { JbAutocompleteOptionsGroup } from 'jb-component-library/jb-autocomplete/types/jb-autocomplete-options-group';
import { equals, toUpper } from 'ramda';
import { Observable } from 'rxjs';

import { CityPairSelector as CityPairSelectorContent } from '../../city-pair-selector/types/city-pair-selector.type';
import { NoMatchText } from '../types';
import { CitySelectorType } from '../types/city-selector-type.enum';
import {
  addAirportToAllSiblingsNeedleInMac,
  addShortNameToAirport,
  filterAirportsUtility,
  generateNoMatchText,
} from '../utils/city-selector.utils';
import {
  citySelectorComponentInitialState,
  CitySelectorState,
  CitySelectorViewModel,
} from './city-selector.state';

@Injectable()
export class CitySelectorStore extends ComponentStore<CitySelectorState> {
  readonly value$: Observable<Airport> = this.select(state => state.value);

  readonly content$: Observable<CityPairSelectorContent> = this.select(
    state => state.content,
  );

  readonly isMobile$: Observable<boolean> = this.select(
    state => state.isMobile,
  );

  readonly errorMessage$: Observable<string> = this.select(
    this.content$,
    content => content.citySelector.errorMessage,
  );

  readonly isLegendShown$: Observable<boolean> = this.select(
    state => !state.hideLegend,
  );

  readonly legends$: Observable<TypedDictionary<Legend>> = this.select(
    this.content$,
    content => {
      if (content?.legends instanceof Array) {
        return content.legends.reduce((accLegends, currentLegend) => {
          return {
            ...accLegends,
            [currentLegend.code.toLowerCase()]: currentLegend,
          };
        }, {});
      } else {
        return { [content.legends.code.toLowerCase()]: content.legends };
      }
    },
  );

  readonly noMatchText$: Observable<NoMatchText> = this.select(
    this.content$,
    content => content.citySelector.noMatchText,
  );

  readonly separatePartnerCities$: Observable<boolean> = this.select(
    state => state.separatePartnerCities,
  );

  readonly airportSuggestions$: Observable<CountryWithAirports[]> = this.select(
    state => state.airportSuggestions,
  );

  readonly citySelectorType$: Observable<CitySelectorType> = this.select(
    state => state.citySelectorType,
  );

  readonly label$: Observable<string> = this.select(
    this.content$,
    this.citySelectorType$,
    (content: CityPairSelectorContent, citySelectorType: CitySelectorType) => {
      return content
        ? citySelectorType === CitySelectorType.ORIGIN
          ? content.from.label
          : content.to.label
        : '';
    },
  );

  readonly placeholder$: Observable<string> = this.select(
    this.content$,
    this.citySelectorType$,
    (content: CityPairSelectorContent, citySelectorType: CitySelectorType) => {
      return content
        ? citySelectorType === CitySelectorType.ORIGIN
          ? content.from.placeholder
          : content.to.placeholder
        : '';
    },
  );

  readonly nearbyAirportCodes$: Observable<string[]> = this.select(
    state => state.nearbyAirportCodes,
  );

  readonly recentAirports$: Observable<Airport[]> = this.select(
    state => state.recentAirports,
  );

  readonly originsFacadeAirports$: Observable<Airport[]> = this.select(
    state => state.originsFacadeAirports,
  );

  readonly originsFacadeAirportsHashMap$: Observable<TypedDictionary<Airport>> =
    this.select(state => state.originsFacadeAirportsHashMap);

  readonly allAirportsSorted$: Observable<Airport[]> = this.select(
    this.airportSuggestions$,
    this.separatePartnerCities$,
    (
      airportSuggestions: CountryWithAirports[],
      separatePartnerCities: boolean,
    ) => {
      if (!airportSuggestions || !airportSuggestions.length) {
        return [];
      }

      return extractAndSortAirports(airportSuggestions, separatePartnerCities);
    },
  );

  readonly allAirportsHashMap$: Observable<TypedDictionary<Airport>> =
    this.select(
      this.allAirportsSorted$,
      (allAirportsSorted: Airport[]): TypedDictionary<Airport> => {
        return keyBy('code', allAirportsSorted);
      },
    );

  readonly allAirports$: Observable<Airport[]> = this.select(
    this.allAirportsSorted$,
    (allAirportsSorted: Airport[]): Airport[] => {
      if (!allAirportsSorted || !allAirportsSorted.length) {
        return [];
      }
      const allAirportsSortedWithoutPm = allAirportsSorted.filter(
        airport => !airport.pm,
      );
      const allPms = allAirportsSorted
        .filter(airport => airport.cc)
        .map(airport => airport.code);
      const airportsWithPm = allAirportsSorted.filter(airport =>
        allPms.includes(airport.pm),
      );

      for (let i = airportsWithPm.length - 1; i > -1; i--) {
        const airport = airportsWithPm[i];
        const pmIndex = allAirportsSortedWithoutPm.findIndex(
          pmAirport => pmAirport.code === airport.pm,
        );
        allAirportsSortedWithoutPm.splice(pmIndex + 1, 0, airport);
      }

      return addAirportToAllSiblingsNeedleInMac(allAirportsSortedWithoutPm);
    },
  );

  readonly groupedAirports$: Observable<JbAutocompleteOptionsGroup[]> =
    this.select(
      this.allAirportsHashMap$,
      this.citySelectorType$,
      this.airportSuggestions$,
      this.separatePartnerCities$,
      this.nearbyAirportCodes$,
      this.recentAirports$,
      (
        allAirportsHashMap: TypedDictionary<Airport>,
        citySelectorType: CitySelectorType,
        airportSuggestions: CountryWithAirports[],
        separatePartnerCities: boolean,
        nearbyAirportCodes: string[],
        recentAirports,
      ): JbAutocompleteOptionsGroup[] => {
        let groupedAirports: JbAutocompleteOptionsGroup[] = [];

        if (airportSuggestions?.length >= 0) {
          const allAirportsSorted = extractAndSortAirports(
            airportSuggestions,
            separatePartnerCities,
          );

          allAirportsHashMap = keyBy('code', allAirportsSorted);

          const allAirportsSortedWithoutPm = allAirportsSorted.filter(
            airport => !airport.pm,
          );
          const allPms = allAirportsSorted
            .filter(airport => airport.cc)
            .map(airport => airport.code);
          const airportsWithPm = allAirportsSorted.filter(airport =>
            allPms.includes(airport.pm),
          );

          for (let i = airportsWithPm.length - 1; i > -1; i--) {
            const airport = airportsWithPm[i];
            const pmIndex = allAirportsSortedWithoutPm.findIndex(
              pmAirport => pmAirport.code === airport.pm,
            );
            allAirportsSortedWithoutPm.splice(pmIndex + 1, 0, airport);
          }

          if (citySelectorType === CitySelectorType.ORIGIN) {
            const nearbyAirports = codesToAirports(
              allAirportsHashMap,
              nearbyAirportCodes,
            );

            const options: JbAutocompleteOption[] = nearbyAirports.map(
              item => ({
                ...item,
                value: item.code,
              }),
            );

            if (nearbyAirports.length > 0) {
              const newNearbyData: JbAutocompleteOptionsGroup = {
                title: 'NEARBY',
                options,
              };

              const nearbyGroupIndex = groupedAirports.findIndex(
                group => group.title === 'NEARBY',
              );

              if (nearbyGroupIndex > -1) {
                groupedAirports = [
                  ...groupedAirports.slice(0, nearbyGroupIndex),
                  newNearbyData,
                  ...groupedAirports.slice(nearbyGroupIndex + 1),
                ];
              } else {
                groupedAirports = [newNearbyData, ...groupedAirports];
              }
            }
          }
        }
        if (recentAirports?.length) {
          const recentGroupIndex = groupedAirports.findIndex(
            group => group.title === 'RECENT',
          );

          const options: JbAutocompleteOption[] = recentAirports.map(item => ({
            ...item,
            value: item.code,
            countryCode: allAirportsHashMap[item.code]?.countryCode,
          }));

          const newRecentGroupData: JbAutocompleteOptionsGroup = {
            title: 'RECENT',
            options,
          };

          if (recentGroupIndex > -1) {
            groupedAirports = [
              ...groupedAirports.slice(0, recentGroupIndex),
              newRecentGroupData,
              ...groupedAirports.slice(recentGroupIndex + 1),
            ];
          } else {
            groupedAirports = [...groupedAirports, newRecentGroupData];
          }
        }

        return groupedAirports;
      },
    );

  readonly noMatchFoundCallback$: Observable<(searchStr: string) => string> =
    this.select(
      this.allAirports$,
      this.originsFacadeAirports$,
      this.originsFacadeAirportsHashMap$,
      this.noMatchText$,
      (
        allAirports: Airport[],
        originsFacadeAirports: Airport[],
        originsFacadeAirportsHashMap: TypedDictionary<Airport>,
        noMatchText: NoMatchText,
      ) =>
        (searchStr: string) => {
          if (!searchStr || !allAirports) {
            return;
          }
          // before investing in expensive filter, see if city code was entered
          if (!!originsFacadeAirportsHashMap?.[toUpper(searchStr)]) {
            return noMatchText.invalidRoute;
          } else {
            const filteredAirportsCollection = filterAirportsUtility(
              originsFacadeAirports,
              originsFacadeAirportsHashMap,
              searchStr,
            );
            if (
              !!filteredAirportsCollection &&
              filteredAirportsCollection.length
            ) {
              return noMatchText.invalidRoute;
            }
          }
          return generateNoMatchText(searchStr, noMatchText.invalidAirport);
        },
    );

  readonly vm$: Observable<CitySelectorViewModel> = this.select(
    this.content$,
    this.allAirports$,
    this.groupedAirports$,
    this.label$,
    this.isLegendShown$,
    this.legends$,
    this.noMatchFoundCallback$,
    this.isMobile$,
    (
      content,
      allAirports,
      groupedAirports,
      label,
      isLegendShown,
      legends,
      noMatchFoundCallback,
      isMobile,
    ) => {
      return {
        modalLinkData: {
          shouldOpenDialog: content?.valueType === 'browseByRegions',
          label:
            content?.valueType === 'browseByRegions'
              ? content.browseByRegions
              : content.title || '',
          url: content?.page,
        },
        isMobile,
        allAirports,
        groupedAirports,
        label,
        isLegendShown,
        noMatchFoundCallback,
        legends,
      };
    },
  );

  readonly setValue = this.updater((state, value: Airport) => {
    return !equals(state.value, value)
      ? {
          ...state,
          value: state.showShortCityName ? addShortNameToAirport(value) : value,
        }
      : state;
  });
  constructor() {
    super(citySelectorComponentInitialState);
  }
}
